import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { initPushDeepLink, syncPushToken, isPushSupported } from '@/services/pushClient';
import { sentinelClient } from '@/services/sentinelClient';
import { isPremiumUser } from '@/services/premium';
import { lazyWithRetry } from '@/utils/lazyWithRetry';

// Shared
import { Header } from '@/ui/components/Header';
import { Footer } from '@/ui/components/Footer';
import { AdminLayout } from '@/ui/components/AdminLayout';
import { PrivateRoute } from '@/ui/components/PrivateRoute';
import ScrollToTop from '@/ui/components/ScrollToTop';
import { api } from '@/controllers/api';
import { trackPageView } from '@/services/analytics';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { SiteSettingsContext } from '@/hooks/useSiteSettings';
import { FounderPopup } from '@/ui/components/FounderPopup';

// Le Loader principal pour les "Chunks"
import { PageLoader } from '@/ui/components/PageLoader';

// Public Screens (Home doit rester synchrone et critique)
import { Home } from '@/ui/screens/Home';
import { WelcomeScreen } from '@/ui/components/WelcomeScreen';

// Pages légales (Très légères, critiques pour SEO initial)
import PrivacyPolicyPage from '@/ui/screens/PrivacyPolicyPage';
import LegalMentionsPage from '@/ui/screens/LegalMentionsPage';
import SubscriptionTermsPage from '@/ui/screens/SubscriptionTermsPage';
import AccountDeletionPage from '@/ui/screens/AccountDeletionPage';
import { PrivacyBanner } from '@/ui/components/PrivacyBanner';

// Public Screens (Lazy Loaded - Pour éviter les bundles de 10Mo)
const GalleryPage = lazyWithRetry(() => import('@/ui/screens/GalleryPage').then(module => ({ default: module.GalleryPage })));
const GearPage = lazyWithRetry(() => import('@/ui/screens/GearPage').then(module => ({ default: module.GearPage })));
const AboutPage = lazyWithRetry(() => import('@/ui/screens/AboutPage').then(module => ({ default: module.AboutPage })));
const ContactPage = lazyWithRetry(() => import('@/ui/screens/ContactPage').then(module => ({ default: module.ContactPage })));
const VideosPage = lazyWithRetry(() => import('@/ui/screens/VideosPage').then(module => ({ default: module.VideosPage })));
const NewsPage = lazyWithRetry(() => import('@/ui/screens/NewsPage').then(module => ({ default: module.NewsPage })));
const EventsPage = lazyWithRetry(() => import('@/ui/screens/EventsPage').then(module => ({ default: module.EventsPage })));
const ResourcesPage = lazyWithRetry(() => import('@/ui/screens/ResourcesPage').then(module => ({ default: module.ResourcesPage })));
const MaintenancePage = lazyWithRetry(() => import('@/ui/screens/MaintenancePage').then(module => ({ default: module.MaintenancePage })));
const PlannerPage = lazyWithRetry(() => import('@/ui/screens/PlannerPage').then(module => ({ default: module.PlannerPage })));
const HelpPage = lazyWithRetry(() => import('@/ui/screens/HelpPage').then(module => ({ default: module.HelpPage })));
const HorizonBuilderPage = lazyWithRetry(() => import('@/ui/screens/HorizonBuilderPage').then(module => ({ default: module.HorizonBuilderPage })));
const ExifsPage = lazyWithRetry(() => import('@/ui/screens/ExifsPage').then(module => ({ default: module.ExifsPage })));


// Auth Sentinel (Lazy Loaded — US 6.1)
const LoginPage = lazyWithRetry(() => import('@/ui/screens/auth/LoginPage').then(module => ({ default: module.LoginPage })));
const RegisterPage = lazyWithRetry(() => import('@/ui/screens/auth/RegisterPage').then(module => ({ default: module.RegisterPage })));
const AccountPage = lazyWithRetry(() => import('@/ui/screens/auth/AccountPage').then(module => ({ default: module.AccountPage })));
const VerifyEmailPage = lazyWithRetry(() => import('@/ui/screens/auth/VerifyEmailPage').then(module => ({ default: module.VerifyEmailPage })));
const ForgotPasswordPage = lazyWithRetry(() => import('@/ui/screens/auth/ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazyWithRetry(() => import('@/ui/screens/auth/ResetPasswordPage').then(module => ({ default: module.ResetPasswordPage })));
const FounderConfirmPage = lazyWithRetry(() => import('@/ui/screens/auth/FounderConfirmPage').then(module => ({ default: module.FounderConfirmPage })));
const TestimonialsPage = lazyWithRetry(() => import('@/ui/screens/TestimonialsPage').then(module => ({ default: module.TestimonialsPage })));
const PlannerDiscoveryPage = lazyWithRetry(() => import('@/ui/screens/PlannerDiscoveryPage').then(module => ({ default: module.PlannerDiscoveryPage })));

// Sentinel — Projets multi-nuits (US 6.4, Lazy Loaded)
const SentinelProjectsPage = lazyWithRetry(() => import('@/ui/screens/sentinel/SentinelProjectsPage').then(module => ({ default: module.SentinelProjectsPage })));
const SentinelProjectDetailPage = lazyWithRetry(() => import('@/ui/screens/sentinel/SentinelProjectDetailPage').then(module => ({ default: module.SentinelProjectDetailPage })));
const SentinelAlertsPage = lazyWithRetry(() => import('@/ui/screens/sentinel/SentinelAlertsPage').then(module => ({ default: module.SentinelAlertsPage })));
const SentinelDashboardPage = lazyWithRetry(() => import('@/ui/screens/sentinel/SentinelDashboardPage').then(module => ({ default: module.SentinelDashboardPage })));
const SentinelCalendarPage = lazyWithRetry(() => import('@/ui/screens/sentinel/SentinelCalendarPage').then(module => ({ default: module.SentinelCalendarPage })));
// Sentinel — Landing publique (US 6.13, indexable)
const SentinelLandingPage = lazyWithRetry(() => import('@/ui/screens/sentinel/SentinelLandingPage').then(module => ({ default: module.SentinelLandingPage })));

// SEO Guides (Lazy Loaded)
const GuidesPage = lazyWithRetry(() => import('@/ui/screens/GuidesPage'));
const GuidePlanification = lazyWithRetry(() => import('@/ui/screens/guides/GuidePlanification'));
const GuideFOV = lazyWithRetry(() => import('@/ui/screens/guides/GuideFOV'));
const GuideFiltres = lazyWithRetry(() => import('@/ui/screens/guides/GuideFiltres'));

// Admin Screens (Lazy Loaded - Pour le Dashoard lourd Chart.js)
const AdminLogin = lazyWithRetry(() => import('@/ui/screens/admin/AdminLogin').then(module => ({ default: module.AdminLogin })));
const AdminGallery = lazyWithRetry(() => import('@/ui/screens/admin/AdminGallery').then(module => ({ default: module.AdminGallery })));
const AdminGear = lazyWithRetry(() => import('@/ui/screens/admin/AdminGear').then(module => ({ default: module.AdminGear })));
const AdminMessages = lazyWithRetry(() => import('@/ui/screens/admin/AdminMessages').then(module => ({ default: module.AdminMessages })));
const AdminProfile = lazyWithRetry(() => import('@/ui/screens/admin/AdminProfile').then(module => ({ default: module.AdminProfile })));
const AdminSettings = lazyWithRetry(() => import('@/ui/screens/admin/AdminSettings').then(module => ({ default: module.AdminSettings })));
const AdminVideos = lazyWithRetry(() => import('@/ui/screens/admin/AdminVideos').then(module => ({ default: module.AdminVideos })));
const AdminNews = lazyWithRetry(() => import('@/ui/screens/admin/AdminNews').then(module => ({ default: module.AdminNews })));
const AdminResources = lazyWithRetry(() => import('@/ui/screens/admin/AdminResources').then(module => ({ default: module.AdminResources })));
const AdminAnalytics = lazyWithRetry(() => import('@/ui/screens/admin/AdminAnalytics').then(module => ({ default: module.AdminAnalytics })));
const AdminTestimonials = lazyWithRetry(() => import('@/ui/screens/admin/AdminTestimonials').then(module => ({ default: module.AdminTestimonials })));
const AdminAstroEvents = lazyWithRetry(() => import('@/ui/screens/admin/AdminAstroEvents').then(module => ({ default: module.AdminAstroEvents })));
const AdminSentinelUsers = lazyWithRetry(() => import('@/ui/screens/admin/AdminSentinelUsers').then(module => ({ default: module.AdminSentinelUsers })));

import { BuyMeCoffeeButton } from '@/ui/components/BuyMeCoffeeButton';
import { SEOManager } from '@/ui/components/SEOManager';

// Composant pour gérer le mode maintenance par route
const RouteGuard: React.FC<{ children: React.ReactNode; maintenanceMode: boolean }> = ({ children, maintenanceMode }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Fallback dynamique
  const MPage = MaintenancePage as React.ComponentType;

  if (maintenanceMode && !isAdminRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MPage />
      </Suspense>
    );
  }

  return <>{children}</>;
};

import { useTranslation } from 'react-i18next';

// Page view tracker - sends analytics on route change
const PageViewTracker: React.FC = () => {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    // Only track public pages (not admin)
    if (!location.pathname.startsWith('/admin')) {
      trackPageView(location.pathname, i18n.language);
    }
  }, [location.pathname, i18n.language]);

  return null;
};

// [US 6.9 phase B] Branche la navigation sur tap d'une notification push (deep link).
// Monté sous le BrowserRouter pour disposer de useNavigate ; no-op sur web (push natif uniquement).
const PushDeepLinkInit: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => { void initPushDeepLink((path) => navigate(path)); }, [navigate]);
  return null;
};

// Ré-enregistre le token push au démarrage (une fois par compte connecté) si le canal inclut le push :
// un token tourné par FCM puis purgé côté serveur laissait le compte sans aucune alerte.
// Premium uniquement (le serveur refuse l'enregistrement en 403 sinon) ; no-op sur web.
const PushTokenSync: React.FC = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const userId = user?.id;
  const premium = isPremiumUser(user);
  useEffect(() => {
    if (!userId || !premium || !isPushSupported()) return;
    void (async () => {
      const prefs = await sentinelClient.getAlertPrefs();
      if (prefs && (prefs.channel === 'push' || prefs.channel === 'both')) await syncPushToken(i18n.language);
    })();
    // i18n.language volontairement hors deps : un changement de langue ne doit pas relancer l'enregistrement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, premium]);
  return null;
};

// Floating BMC button - on high-value public pages with contextual messages
const FloatingSupport: React.FC = () => {
  const location = useLocation();

  // Mapping route → variant pour messages contextuels
  const variantMap: Record<string, 'planner' | 'gallery' | 'resources' | 'videos'> = {
    '/planner': 'planner',
    '/galerie': 'gallery',
    '/ressources': 'resources',
    '/videos': 'videos'
  };

  const variant = variantMap[location.pathname];
  if (!variant) return null;

  return <BuyMeCoffeeButton floating variant={variant} />;
};

import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { BottomNavigation } from '@/ui/components/BottomNavigation';
import { MobileMenu } from '@/ui/components/MobileMenu';
import { NightVisionProvider } from '@/ui/components/NightVisionContext';

const App: React.FC = () => {
  const isNative = Capacitor.isNativePlatform();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  // [Interrupteur Sentinel] Ouverture de Sentinel au public. Initialisé depuis le dernier état connu
  // (localStorage) pour éviter un flash de la promo au chargement ; défaut FERMÉ au tout premier passage.
  const [sentinelPublic, setSentinelPublic] = useState<boolean>(() => {
    try { return localStorage.getItem('ssiSentinelPublic') === 'true'; } catch { return false; }
  });
  // Vrai dès que l'état d'ouverture est connu (mémorisé, ou réponse de /api/settings). Sans ça, un
  // nouveau visiteur (Googlebot, lien partagé) était redirigé de /sentinel vers / avant la réponse.
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(() => {
    try { return localStorage.getItem('ssiSentinelPublic') !== null; } catch { return false; }
  });
  const [loading, setLoading] = useState(isNative);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isNative) {
      // Barre de statut : fond sombre et icônes claires fixés par le thème Android (styles.xml), sans plugin.
      // Bord à bord (Android 15+) : la place est réservée par les env(safe-area-inset-top) de index.css
      // et des écrans natifs.
      document.body.classList.add('is-native');

      // Blocage du pinch-to-zoom au niveau JavaScript pour une protection robuste
      const preventZoom = (e: TouchEvent) => {
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      };

      document.addEventListener('touchstart', preventZoom, { passive: false });

      // [Clavier] targetSdk >= 35 : Android impose le bord-à-bord et ne redimensionne plus la fenêtre
      // à l'ouverture du clavier (windowSoftInputMode="adjustResize" sans effet). Le champ saisi passe
      // donc sous le clavier. Le visualViewport, lui, reflète bien la zone réellement visible : on
      // ramène le champ focalisé dedans. Aucun code natif — deux tentatives d'insets côté MainActivity
      // ont cassé la mise en page de Capacitor (2026-09-23).
      const vv = window.visualViewport;
      let scrollTimer: number | undefined;
      const keepFocusVisible = () => {
        const el = document.activeElement as HTMLElement | null;
        if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
        // Laisse le clavier finir son animation avant de mesurer.
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          const visibleTop = vv ? vv.offsetTop : 0;
          const visibleHeight = vv ? vv.height : window.innerHeight;
          const rect = el.getBoundingClientRect();
          // scrollIntoView('center') centre dans la page entière : il ignore l'en-tête et la barre de
          // navigation fixes, et laisse le champ derrière l'en-tête. On vise donc explicitement 45 % de
          // la hauteur visible — sous l'en-tête, au-dessus du clavier.
          const desiredTop = visibleTop + visibleHeight * 0.45;
          const delta = rect.top - desiredTop;
          if (Math.abs(delta) < 24) return; // déjà bien placé : ne rien bouger

          // Le contenu défile dans un conteneur interne selon les écrans : remonter jusqu'au premier
          // parent réellement défilable, sinon la page.
          let scroller: HTMLElement | null = el.parentElement;
          while (scroller) {
            const overflowY = getComputedStyle(scroller).overflowY;
            if (/(auto|scroll)/.test(overflowY) && scroller.scrollHeight > scroller.clientHeight) break;
            scroller = scroller.parentElement;
          }
          if (scroller) scroller.scrollBy({ top: delta, behavior: 'smooth' });
          else window.scrollBy({ top: delta, behavior: 'smooth' });
        }, 250);
      };

      document.addEventListener('focusin', keepFocusVisible);
      vv?.addEventListener('resize', keepFocusVisible);

      // Injection de la balise viewport restrictive uniquement en mode natif
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
      }

      return () => {
        document.removeEventListener('touchstart', preventZoom);
        document.removeEventListener('focusin', keepFocusVisible);
        vv?.removeEventListener('resize', keepFocusVisible);
        window.clearTimeout(scrollTimer);
        // On remet le viewport standard au cas où
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, viewport-fit=cover');
        }
      };
    } else {
      document.body.classList.remove('is-native');
    }
  }, [isNative]);

  const checkMaintenance = async () => {
    try {
      const settings = await api.settings.get();
      // Le mode maintenance ne s'applique qu'au site web (l'app native n'est jamais coupée).
      if (!isNative) setMaintenanceMode(settings.maintenanceMode);
      // [Interrupteur Sentinel] Le flag d'ouverture, lui, vaut aussi pour le natif (MobileMenu / bouton
      // « Suivre » du Planner). On mémorise le dernier état connu pour éviter un flash au prochain démarrage.
      const isOpen = !!settings.sentinelPublic; // robuste au fallback hors-ligne (champ absent → fermé)
      setSentinelPublic(isOpen);
      try { localStorage.setItem('ssiSentinelPublic', isOpen ? 'true' : 'false'); } catch { /* stockage indisponible : on ignore */ }
    } catch (e) {
      console.error("Failed to load site settings:", e?.message || e);
    } finally {
      // Même en erreur : l'état reste « fermé » mais connu, on ne bloque jamais l'affichage.
      setSettingsLoaded(true);
      if (!isNative) setLoading(false);
    }
  };

  useEffect(() => {
    checkMaintenance();

    if (isNative) {
      // Masquer le splash screen natif pour laisser place au WelcomeScreen React
      SplashScreen.hide().catch(e => console.warn('Could not hide splash screen', e));

      // Sur mobile, on garde le welcome screen affiché au moins 2.5s pour l'effet premium
      const timer = setTimeout(() => {
        setLoading(false);
      }, 3000); // Augmenté à 3s pour bien voir l'effet
      return () => clearTimeout(timer);
    }
  }, [isNative]);

  if (loading && isNative) return <WelcomeScreen />;

  return (
    <AuthProvider>
    <SiteSettingsContext.Provider value={{ sentinelPublic, settingsLoaded }}>
    <NightVisionProvider>
    <BrowserRouter>
      <ScrollToTop />
      <SEOManager />
      <PageViewTracker />
      <PushDeepLinkInit />
      <PushTokenSync />
      {/* [US 6.15] Popup fondateur — natif uniquement (forçable en web via localStorage, cf. composant) */}
      <FounderPopup />
      {!isNative && <FloatingSupport />}
      <RouteGuard maintenanceMode={maintenanceMode}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={isNative ? <Navigate to="/planner" replace /> : <><Header /><Home /></>} />
            <Route path="/galerie" element={<>{!isNative && <Header />}<GalleryPage /></>} />
            <Route path="/gallery" element={<Navigate to="/galerie" replace />} />
            <Route path="/actualites" element={<>{!isNative && <Header />}<NewsPage /></>} />
            <Route path="/news" element={<Navigate to="/actualites" replace />} />
            <Route path="/evenements" element={<>{!isNative && <Header />}<EventsPage />{!isNative && <Footer />}</>} />
            <Route path="/events" element={<Navigate to="/evenements" replace />} />
            <Route path="/materiel" element={<>{!isNative && <Header />}<GearPage /></>} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/a-propos" element={isNative ? <Navigate to="/about" replace /> : <><Header /><AboutPage /></>} />
            <Route path="/contact" element={<>{!isNative && <Header />}<ContactPage /></>} />
            <Route path="/videos" element={<>{!isNative && <Header />}<VideosPage />{!isNative && <Footer />}</>} />
            <Route path="/ressources" element={<>{!isNative && <Header />}<ResourcesPage /></>} />
            <Route path="/planner" element={<><Header hidden={isNative} /><PlannerPage />{!isNative && <Footer />}</>} />
            <Route path="/horizon-generator" element={<HorizonBuilderPage />} />
            <Route path="/exifs-acquisition" element={<ExifsPage />} />
            {/* [US 6.16] Témoignages & preuve sociale — indexable, dans le sitemap */}
            <Route path="/temoignages" element={<TestimonialsPage />} />
            {/* [US 6.18] Tour produit Planner — indexable, dans le sitemap */}
            <Route path="/decouverte-planner" element={<PlannerDiscoveryPage />} />

            {/* SEO Guides */}
            <Route path="/guides" element={<GuidesPage />} />
            <Route path="/guide-planification" element={<GuidePlanification />} />
            <Route path="/guide-fov" element={<GuideFOV />} />
            <Route path="/guide-filtres" element={<GuideFiltres />} />

            {/* Compte utilisateur Sentinel (US 6.1) — hors sitemap, noindex */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* [US 6.15] Confirmation d'activation fondateur (lien e-mail) — hors sitemap, noindex */}
            <Route path="/founder-confirm" element={<FounderConfirmPage />} />

            {/* Sentinel — Landing publique (US 6.13) — indexable, dans le sitemap.
                [Interrupteur Sentinel] Fermé → redirection vers l'accueil (la landing n'est plus servie). */}
            <Route path="/sentinel" element={sentinelPublic ? <SentinelLandingPage /> : settingsLoaded ? <Navigate to="/" replace /> : null} />

            {/* Sentinel — Rapport du soir (US 6.9 inc.2) — connecté, hors sitemap, noindex */}
            <Route path="/sentinel/tonight" element={<><Header hidden={isNative} /><SentinelDashboardPage />{!isNative && <Footer />}</>} />
            {/* Sentinel — Mes projets (US 6.4) — connecté, hors sitemap, noindex */}
            <Route path="/sentinel/projects" element={<><Header hidden={isNative} /><SentinelProjectsPage />{!isNative && <Footer />}</>} />
            <Route path="/sentinel/projects/:id" element={<><Header hidden={isNative} /><SentinelProjectDetailPage />{!isNative && <Footer />}</>} />
            {/* Sentinel — Calendrier 14 nuits (US 6.10) — connecté, hors sitemap, noindex */}
            <Route path="/sentinel/calendar" element={<><Header hidden={isNative} /><SentinelCalendarPage />{!isNative && <Footer />}</>} />
            {/* Sentinel — Réglages des alertes (US 6.9) — connecté, hors sitemap, noindex */}
            <Route path="/sentinel/alerts" element={<><Header hidden={isNative} /><SentinelAlertsPage />{!isNative && <Footer />}</>} />

            {/* Pages légales */}
            <Route path="/confidentialite" element={<PrivacyPolicyPage />} />
            <Route path="/mentions-legales" element={<LegalMentionsPage />} />
            <Route path="/cgv" element={<SubscriptionTermsPage />} />
            <Route path="/suppression-compte" element={<AccountDeletionPage />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />

            <Route path="/admin" element={<Navigate to="/admin/statistiques" replace />} />
            <Route path="/admin/actualites" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminNews /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/galerie" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminGallery /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/materiel" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminGear /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/messages" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminMessages /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/parametres" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminSettings /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/videos" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminVideos /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/ressources" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminResources /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/profil" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminProfile /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/statistiques" element={<PrivateRoute><AdminLayout><AdminAnalytics /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/temoignages" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminTestimonials /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/astro-events" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminAstroEvents /></AdminLayout></PrivateRoute>} />
            <Route path="/admin/abonnes" element={<PrivateRoute requiredRole="admin"><AdminLayout><AdminSentinelUsers /></AdminLayout></PrivateRoute>} />
          </Routes>
        </Suspense>
      </RouteGuard>
      {!isNative && <PrivacyBanner />}
      
      {/* Mobile Native Navigation */}
      {isNative && (
        <>
          <BottomNavigation 
            isNative={isNative} 
            onMoreClick={() => setIsMobileMenuOpen(true)} 
          />
          <MobileMenu 
            isOpen={isMobileMenuOpen} 
            onClose={() => setIsMobileMenuOpen(false)} 
            hideButton={true}
          />
        </>
      )}
    </BrowserRouter>
    </NightVisionProvider>
    </SiteSettingsContext.Provider>
    </AuthProvider>
  );
};

export default App;
